// src/file-constants.ts
var SUPPORTED_DOCUMENT_TYPES = {
  PDF: "application/pdf",
  DOC: "application/msword",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  TXT: "text/plain",
  RTF: "application/rtf",
  ODT: "application/vnd.oasis.opendocument.text",
  MARKDOWN: "text/markdown"
};
var SUPPORTED_IMAGE_TYPES = {
  JPEG: "image/jpeg",
  JPG: "image/jpg",
  PNG: "image/png",
  WEBP: "image/webp",
  GIF: "image/gif",
  BMP: "image/bmp",
  TIFF: "image/tiff"
};
var SUPPORTED_AUDIO_TYPES = {
  MP3: "audio/mpeg",
  WAV: "audio/wav",
  M4A: "audio/mp4",
  WEBM: "audio/webm",
  OGG: "audio/ogg",
  FLAC: "audio/flac",
  AAC: "audio/aac"
};
var SUPPORTED_VIDEO_TYPES = {
  MP4: "video/mp4",
  WEBM: "video/webm",
  MOV: "video/quicktime",
  AVI: "video/x-msvideo"
};
var ALL_SUPPORTED_TYPES = {
  ...SUPPORTED_DOCUMENT_TYPES,
  ...SUPPORTED_IMAGE_TYPES,
  ...SUPPORTED_AUDIO_TYPES,
  ...SUPPORTED_VIDEO_TYPES
};
var UPLOAD_LIMITS = {
  // Limites de arquivo (bytes)
  FILE_SIZES: {
    AUDIO: 500 * 1024 * 1024,
    // 500MB
    DOCUMENT: 500 * 1024 * 1024,
    // 500MB
    IMAGE: 500 * 1024 * 1024,
    // 500MB
    VIDEO: 500 * 1024 * 1024
    // 500MB
  },
  // Limites de quantidade por campanha
  FILE_COUNTS: {
    DOCUMENT: 10,
    // máximo 10 documentos por campanha
    IMAGE: 25,
    // máximo 25 imagens por campanha
    AUDIO: -1,
    // ilimitado
    VIDEO: 5
    // máximo 5 vídeos por campanha
  },
  // Formatos aceitos
  MIME_TYPES: {
    AUDIO: Object.values(SUPPORTED_AUDIO_TYPES),
    DOCUMENT: Object.values(SUPPORTED_DOCUMENT_TYPES),
    IMAGE: Object.values(SUPPORTED_IMAGE_TYPES),
    VIDEO: Object.values(SUPPORTED_VIDEO_TYPES)
  },
  // Extensões
  EXTENSIONS: {
    AUDIO: [".mp3", ".wav", ".m4a", ".aac", ".ogg", ".webm"],
    DOCUMENT: [".pdf", ".doc", ".docx", ".txt", ".md", ".rtf", ".odt"],
    IMAGE: [".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".tiff"],
    VIDEO: [".mp4", ".webm", ".mov", ".avi"]
  }
};
var FILE_PROCESSING_STATUS = {
  // Initial States
  IDLE: "idle",
  QUEUED: "queued",
  ERROR: "error",
  // Upload Phase
  UPLOADING: "uploading",
  UPLOAD_FAILED: "upload_failed",
  // Processing Phase
  PROCESSING: "processing",
  PROCESSING_FAILED: "processing_failed",
  // Transcription Phase (audio specific)
  TRANSCRIBING: "transcribing",
  TRANSCRIPTION_COMPLETED: "transcription_completed",
  TRANSCRIPTION_FAILED: "transcription_failed",
  // RAG Phase (document specific)
  RAG_QUEUED: "rag_queued",
  RAG_COMPLETED: "rag_completed",
  RAG_FAILED: "rag_failed",
  // Summary Phase (audio specific)
  SUMMARY_QUEUED: "summary_queued",
  SUMMARY_COMPLETED: "summary_completed",
  SUMMARY_FAILED: "summary_failed",
  // Final States
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled"
};
var UPLOAD_CONFIG = {
  // Credit rate fallbacks: import DEFAULT_UPLOAD_CREDIT_RATES from @tessel/credit-service
  // (single source — DEFAULT_CREDIT_CONFIG.frontendRates). Web uses usePricing() when online.
  // Timeouts (estava espalhado sem padrão)
  TIMEOUTS: {
    UPLOAD: 30 * 1e3,
    // 30s
    PROCESSING: 5 * 60 * 1e3,
    // 5min
    TRANSCRIPTION: 10 * 60 * 1e3
    // 10min
  },
  // Chunking (lógica duplicada)
  CHUNKING: {
    AUDIO_MAX_DURATION: 3600,
    // 1 hora
    DOCUMENT_MAX_SIZE: 10 * 1024 * 1024,
    // 10MB
    IMAGE_MAX_SIZE: 5 * 1024 * 1024,
    // 5MB
    CHUNK_OVERLAP: 200
    // caracteres
  }
};
var SECURITY_VALIDATIONS = {
  // Extensões proibidas por segurança
  BLOCKED_EXTENSIONS: [
    ".exe",
    ".bat",
    ".cmd",
    ".scr",
    ".pif",
    ".com",
    ".jar",
    ".js",
    ".vbs",
    ".ps1",
    ".sh",
    ".php",
    ".asp",
    ".jsp"
  ],
  // Magic numbers para validação de tipo real
  MAGIC_NUMBERS: {
    PDF: [37, 80, 68, 70],
    // %PDF
    PNG: [137, 80, 78, 71, 13, 10, 26, 10],
    // PNG signature
    JPEG: [255, 216, 255],
    // JPEG
    MP3: [73, 68, 51],
    // ID3
    WAV: [82, 73, 70, 70]
    // RIFF
  },
  // Tamanho máximo de nome de arquivo
  MAX_FILENAME_LENGTH: 255,
  // Caracteres proibidos em nomes de arquivo (avoiding control characters with explicit char codes)
  FORBIDDEN_CHARS: /[<>:"/\\|?*]/g
};
var FILE_TYPE_CATEGORIES = {
  DOCUMENT: "document",
  IMAGE: "image",
  AUDIO: "audio",
  VIDEO: "video"
};
var validateFileSize = (file, fileType) => {
  const maxSize = UPLOAD_LIMITS.FILE_SIZES[fileType.toUpperCase()];
  return file.size <= maxSize;
};
var validateFileMimeType = (mimeType, fileType) => {
  const fileTypeUpper = fileType.toUpperCase();
  const allowedTypes = UPLOAD_LIMITS.MIME_TYPES[fileTypeUpper];
  return allowedTypes.some((type) => type === mimeType);
};
var getFileTypeCategory = (mimeType) => {
  if (Object.values(SUPPORTED_DOCUMENT_TYPES).includes(mimeType)) {
    return FILE_TYPE_CATEGORIES.DOCUMENT;
  }
  if (Object.values(SUPPORTED_IMAGE_TYPES).includes(mimeType)) {
    return FILE_TYPE_CATEGORIES.IMAGE;
  }
  if (Object.values(SUPPORTED_AUDIO_TYPES).includes(mimeType)) {
    return FILE_TYPE_CATEGORIES.AUDIO;
  }
  if (Object.values(SUPPORTED_VIDEO_TYPES).includes(mimeType)) {
    return FILE_TYPE_CATEGORIES.VIDEO;
  }
  return "unknown";
};
var formatFileSize = (bytes) => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};
var getFileTypeName = (mimeType) => {
  const typeNames = {
    "application/pdf": "PDF Document",
    "application/msword": "Word Document",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Document",
    "text/plain": "Text File",
    "application/rtf": "RTF Document",
    "application/vnd.oasis.opendocument.text": "OpenDocument Text",
    "text/markdown": "Markdown File",
    "image/jpeg": "JPEG Image",
    "image/jpg": "JPEG Image",
    "image/png": "PNG Image",
    "image/webp": "WebP Image",
    "image/gif": "GIF Image",
    "image/bmp": "BMP Image",
    "image/tiff": "TIFF Image",
    "audio/mpeg": "MP3 Audio",
    "audio/wav": "WAV Audio",
    "audio/mp4": "M4A Audio",
    "audio/webm": "WebM Audio",
    "audio/ogg": "OGG Audio",
    "audio/flac": "FLAC Audio",
    "audio/aac": "AAC Audio",
    "video/mp4": "MP4 Video",
    "video/webm": "WebM Video",
    "video/quicktime": "MOV Video",
    "video/x-msvideo": "AVI Video"
  };
  return typeNames[mimeType] || "Unknown File Type";
};
var getAcceptAttribute = (fileType) => {
  if (!fileType || fileType === "all") {
    return Object.values(ALL_SUPPORTED_TYPES).join(",");
  }
  return UPLOAD_LIMITS.MIME_TYPES[fileType.toUpperCase()].join(",");
};
var FileSecurityValidator = class _FileSecurityValidator {
  static validateFileComplete(file, fileType) {
    const sizeValid = validateFileSize(file, fileType);
    const typeValid = validateFileMimeType(file.type, fileType);
    const nameValid = _FileSecurityValidator.validateFileName(file.name);
    return {
      isValid: sizeValid && typeValid && nameValid.isValid,
      errors: [
        ...!sizeValid ? [
          `File size exceeds ${formatFileSize(UPLOAD_LIMITS.FILE_SIZES[fileType.toUpperCase()])} limit`
        ] : [],
        ...!typeValid ? ["File type not supported"] : [],
        ...!nameValid.isValid ? [nameValid.error || "Invalid filename"] : []
      ]
    };
  }
  static validateFileName(fileName) {
    if (fileName.length > SECURITY_VALIDATIONS.MAX_FILENAME_LENGTH) {
      return { isValid: false, error: "Filename too long" };
    }
    if (SECURITY_VALIDATIONS.FORBIDDEN_CHARS.test(fileName)) {
      return { isValid: false, error: "Filename contains invalid characters" };
    }
    const extension = fileName.toLowerCase().split(".").pop();
    if (extension && SECURITY_VALIDATIONS.BLOCKED_EXTENSIONS.includes(`.${extension}`)) {
      return {
        isValid: false,
        error: "File extension not allowed for security reasons"
      };
    }
    return { isValid: true };
  }
  static validateFileBatch(files, fileType) {
    const allErrors = [];
    let allValid = true;
    for (const file of files) {
      const result = _FileSecurityValidator.validateFileComplete(file, fileType);
      if (!result.isValid) {
        allValid = false;
        allErrors.push(
          ...result.errors.map((error) => `${file.name}: ${error}`)
        );
      }
    }
    return {
      isValid: allValid,
      errors: allErrors
    };
  }
};

export {
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_AUDIO_TYPES,
  SUPPORTED_VIDEO_TYPES,
  ALL_SUPPORTED_TYPES,
  UPLOAD_LIMITS,
  FILE_PROCESSING_STATUS,
  UPLOAD_CONFIG,
  SECURITY_VALIDATIONS,
  FILE_TYPE_CATEGORIES,
  validateFileSize,
  validateFileMimeType,
  getFileTypeCategory,
  formatFileSize,
  getFileTypeName,
  getAcceptAttribute,
  FileSecurityValidator
};
//# sourceMappingURL=chunk-HMKEFZ6C.js.map